<script setup lang="ts">
import { toRef, useId } from 'vue';
import { useDialogFocus } from '../composables/useDialogFocus';

const props = withDefaults(
  defineProps<{ open: boolean; title: string; busy?: boolean; theme?: 'dark' | 'light' }>(),
  { busy: false, theme: undefined },
);

const emit = defineEmits<{ close: [] }>();

const titleId = useId();
const panelId = useId();

function requestClose(): void {
  if (!props.busy) {
    emit('close');
  }
}

const { onKeydown } = useDialogFocus({
  open: toRef(props, 'open'),
  panelId,
  onClose: requestClose,
  canClose: () => !props.busy,
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="props.open" class="modal" :data-theme="props.theme" @keydown="onKeydown">
        <div class="modal__scrim" @click="requestClose" />
        <div
          :id="panelId"
          class="modal__panel"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
        >
          <header class="modal__header">
            <h2 :id="titleId" class="modal__title">{{ props.title }}</h2>
            <button
              type="button"
              class="modal__close"
              aria-label="Close dialog"
              :disabled="props.busy"
              @click="requestClose"
            >
              ×
            </button>
          </header>
          <div class="modal__body"><slot /></div>
          <footer v-if="$slots.actions" class="modal__actions"><slot name="actions" /></footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: grid;
  place-items: center;
  padding: var(--space-4);
}

.modal__scrim {
  position: absolute;
  inset: 0;
  background: var(--surface-overlay);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}

.modal__panel {
  position: relative;
  width: min(480px, 100%);
  max-height: 85dvh;
  overflow-y: auto;
  padding: var(--space-6);
  background: var(--surface-raised);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
}

.modal__title {
  font-size: var(--text-lg);
}

.modal__close {
  padding: 0 var(--space-2);
  font-size: var(--text-xl);
  line-height: 1;
  color: var(--text-muted);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.modal__close:hover:not(:disabled) {
  color: var(--text-primary);
}

.modal__body {
  color: var(--text-secondary);
  font-size: var(--text-sm);
}

.modal__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--duration-normal) var(--ease-out);
}

.modal-enter-active .modal__panel,
.modal-leave-active .modal__panel {
  transition: transform var(--duration-normal) var(--ease-spring);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal__panel {
  transform: scale(0.95) translateY(12px);
}

@media (max-width: 640px) {
  .modal__actions {
    flex-direction: column-reverse;
  }
}
</style>
